import { Box, Card, Checkbox, CrudMode, DataDisplay, Grid, GridBreak, Stack, Switch, Typography, UpdateIconButton, ValidationErrors, newToast, pn, useElementBreakpoints } from "@compass/core";
import { DataModelInformation, Input, SubmitButton, SubmitIconButton } from "components";
import { DataModelEnum, ForgotCredential, ForgotCredentialType, LkRoleId, Permission, PermissionAction, User, UserDto, UserEditResource, UserViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ApiService from "services/apiService";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAppTheme, setAppToast } from "redux/reducers/appSlice";
import { loginPageRoute } from "pages/auth/LoginPage/LoginPage";
import { useEffect, useState } from "react";
import { RootState } from "redux/store";
import { isAuthorized } from "services";

export const BasicInfo = (props: {
  crudMode: CrudMode;
  userDto: UserDto;
  appUserRoleId: number | undefined;
  userMembership: string;
  setUserDto: (value: UserDto) => void;
  viewResource: UserViewResource;
  reroute?: (userMembership: string, crudMode: CrudMode) => void;
  editResource: UserEditResource;
  validationErrors: ValidationErrors;
  save?: () => void;
  unsavedChanges?: boolean;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appTenantName = useSelector((state: RootState) => state.app.tenantName);
  const loggedInUserId = useSelector((state: RootState) => state.app.userId);
  const theme = useSelector((state: RootState) => state.app.theme);

  const [forgotCredential, setForgotCredential] = useState<ForgotCredential>({
    tenantName: appTenantName,
    forgotCredentialType: ForgotCredentialType.ForgotPassword,
    email: props.userDto.user!.email
  } as ForgotCredential);

  useEffect(() => {
    if (props.userDto.user! && appTenantName && props.userDto.user!.email) {
      setForgotCredential({
        tenantName: appTenantName!,
        forgotCredentialType: ForgotCredentialType.ForgotPassword,
        email: props.userDto.user!.email
      });
    }
  }, [props.userDto]);

  const emailField = () => {
    var propertyName = pn<User>("email");
    var property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.userDto.user!.email}</DataDisplay>
    ) : (
      <Input
        disabled={props.userMembership != "0" && props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.userDto.user!.email}
        onChange={(e) =>
          props.setUserDto(
            produce(props.userDto, (draft) => {
              draft.user!.email = e.target.value;
            })
          )
        }
      />
    );
  };

  const usernameField = () => {
    var propertyName = pn<User>("username");
    var property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.userDto.user!.username}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.userDto.user!.username}
        onChange={(e) =>
          props.setUserDto(
            produce(props.userDto, (draft) => {
              draft.user!.username = e.target.value;
            })
          )
        }
      />
    );
  };

  const lastNameField = () => {
    var propertyName = pn<User>("lastName");
    var property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.userDto.user!.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.userDto.user!.lastName}
        onChange={(e) =>
          props.setUserDto(
            produce(props.userDto, (draft) => {
              draft.user!.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const darkModeField = () => {
    // TODO add this in the DB somewhere
    // var propertyName = pn<User>("canConfigureAndUseTexting");
    // var property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label="Dark Mode">{theme}</DataDisplay>
    ) : (
      <Switch
        label="Dark Mode"
        checked={theme == "Dark"}
        onChange={(e) => {
          if (theme == "Dark") {
            dispatch(setAppTheme("Light"));
          }
          else {
            dispatch(setAppTheme("Dark"));
          }
        }
        }
      />
    );
  };

  const authV2Field = () => {
    return props.crudMode === "view" ? (
      <DataDisplay label="Enrolled in Auth V2">{theme}</DataDisplay>
    ) : (
      <Switch
        label="Enrolled in Auth V2"
        checked={theme == "Dark"}
        onChange={(e) => {
          if (theme == "Dark") {
            dispatch(setAppTheme("Light"));
          }
          else {
            dispatch(setAppTheme("Dark"));
          }
        }
        }
      />
    );
  };

  const firstNameField = () => {
    var propertyName = pn<User>("firstName");
    var property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.userDto.user!.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.userDto.user!.firstName}
        onChange={(e) =>
          props.setUserDto(
            produce(props.userDto, (draft) => {
              draft.user!.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const postForgotPassword = () => {
    ApiService.auth
      .postForgotCredential(forgotCredential)
      .then(() => {
        dispatch(setAppToast(newToast("success")));
        navigate({
          pathname: `/${loginPageRoute.getPath()}`,
        });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
      });
  };

  return (
    <Card header={
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <PersonOutlineOutlinedIcon></PersonOutlineOutlinedIcon>
          <Typography variant="inherit" paddingLeft={"5px"}>User Information</Typography>
        </Stack>
      </Stack>
    } fullHeight>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {firstNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {lastNameField()}
        </Grid>

        <Grid item xs={mediumFieldBreakpoints}>
          {emailField()}
        </Grid>

        <Grid item xs={mediumFieldBreakpoints}>
          {usernameField()}
        </Grid>

        <GridBreak />

        {props.appUserRoleId == LkRoleId.Cobro && (
          <Grid item xs={mediumFieldBreakpoints}>
            {darkModeField()}
          </Grid>
        )}

        <GridBreak />

        {loggedInUserId == props.userDto.user!.id && (
          <Grid item xs={12} display="flex" justifyContent="center" marginTop="10px">
            <SubmitButton onClick={postForgotPassword} label="Reset Password" sx={{ height: "50px" }} />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
